import React, { useCallback, useEffect, useState } from 'react';
import { FaBuilding, FaFileExcel, FaHashtag, FaPaperPlane, FaSave, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ErpTemplate from '../../components/ErpTemplate';
import CabecalhoEventoAereo from '../../components/CabecalhoEventoAereo';
import { useAtendimentoAereo } from '../../components/AtendimentoAereo';
import ConfirmarAcaoModal from '../../components/ConfirmarAcaoModal';
import PlanilhaEmissaoAereo, {
    AbasDaPlanilha, ChipsLegenda, SelectOrigem, TotaisPlanilha, baixarBlob, filtrosSemEquivalente, passaNosFiltros, textoMaisNovo,
} from '../../components/PlanilhaEmissaoAereo';
import api from '../../services/api';
import notify from '../../services/notify';
import { useSetFullBreadcrumbTrail } from '../../context/BreadcrumbContext';
import '../../css/financeiro-parcela-pagar.css';

const API = process.env.REACT_APP_API_URL;

// Cancelada pelo Aéreo: só ela pede de novo, com entrada nova (`abreLinhaNova` no back). A marca
// "Cancelamento de emissão solicitado" do RSVP não conta: a linha segue com o Aéreo, e o RSVP a devolve
// pelo Reenviar.
const cancelada = (e) => !!e && e.situacao === 'cancelado';

// Quem a requisição leva de fato, dos marcados: o primeiro pedido e o cancelado pedido de novo, cada um
// com entrada nova. O resto está marcado mas fica como está — a marca não recria o status que o Aéreo
// deu, e a observação nova vai na entrada que ele tem. A troca de voo e a volta da pendência vão pelo
// Reenviar da linha, na mesma linha. A mesma conta do back (`abreLinhaNova`), que é quem decide.
const vaiNaRequisicao = (p) => !p.emissao || cancelada(p.emissao);

// Spinner do `.fin-pp` no tamanho do ícone de botão.
const SpinnerBotao = () => (
    <span className="fin-pp__spinner" style={{ width: 11, height: 11, borderWidth: 2, margin: '0 5px 0 0', verticalAlign: '-1px' }} />
);

// O pedido que o RSVP salvou e não solicitou, de volta ao estado da tela: a marca de quem tem pedido
// salvo e a observação dele. A observação, o cancelamento e o Reenviar salvos numa linha que o Aéreo já
// tem vêm na própria linha, com o rascunho do RSVP por cima (`rsvpPendente`). `temSalvo`: há pedido
// salvo; sem nenhum, a tela marca todos, como sempre.
const restaurarSalvo = (lista) => {
    const observacoes = {};
    const comPedido = new Set();
    lista.forEach(p => {
        if (!p.pedidoSalvo) return;
        comPedido.add(p.id);
        if (String(p.pedidoSalvo.observacaoRsvp ?? '').trim()) observacoes[p.id] = p.pedidoSalvo.observacaoRsvp;
    });
    return { observacoes, comPedido, temSalvo: comPedido.size > 0 };
};

/**
 * Solicitar Emissão de Aéreo (RSVP): a planilha de emissão do evento, uma linha por entrada de cada
 * participante, com a anterior visível e só leitura acima da atual. As linhas ficam agrupadas pela
 * requisição em que o participante entrou pela primeira vez, e quem ainda não foi solicitado fica no
 * fim, sob "Não solicitados". Cada linha traz o status, a observação e os valores que o Aéreo
 * preencheu e a cópia editada dos bilhetes. O RSVP marca quem vai e solicita: cada clique abre uma requisição
 * numerada (1ª, 2ª, 3ª), o Aéreo recebe o e-mail e a tela de Emissão libera. Depois da primeira
 * resposta do Aéreo, a observação que o RSVP escreve na linha edita a da entrada que ela tem;
 * depois de emitido, não.
 *
 * O Salvar grava sem mandar nada ao Aéreo. O pedido novo fica só nesta tela, que o restaura ao abrir
 * (`restaurarSalvo`), em A EMITIR com o "Não enviado", até o Solicitar abrir a requisição; a observação,
 * o cancelamento e o Reenviar numa linha que o Aéreo já tem ficam no rascunho do RSVP, que é um só para
 * todo o RSVP. Cada Solicitar grava a tela inteira e manda só o dele, com o e-mail: o do outro botão
 * continua salvo. Do Aéreo, a tela mostra só o que ele enviou.
 *
 * O Reenviar da linha devolve ao Aéreo, na mesma linha e em A EMITIR, a que ele deixou com outro status
 * (as pendências) ou a que tem o voo trocado, com a observação do RSVP. Vai no Solicitar Emissão de Aéreo.
 */
export default function CadastroEventoEmissaoEnvioAereo({ match }) {
    const idEvento = match.params.idEvento;
    const token = localStorage.getItem('token');
    // Quem atende o evento no Aéreo: o atalho do cabeçalho, só para o Aéreo (que abre esta tela para ler) e o admin.
    const atendimento = useAtendimentoAereo(idEvento);
    const [dados, setDados] = useState({ participantes: [], legenda: {}, requisicoes: [], podeEnviar: false, liberadaEmissao: false });
    const [header, setHeader] = useState({});
    const [carregando, setCarregando] = useState(true);
    const [busca, setBusca] = useState('');
    const [filtro, setFiltro] = useState(null);
    const [filtroOrigem, setFiltroOrigem] = useState(null);
    // A aba da planilha, no rodapé da tabela (`AbasDaPlanilha`): o id de uma das que o Aéreo criou no evento, ou
    // null para a Todos Participantes. O RSVP escolhe, não cria nem exclui.
    const [abaPlanilha, setAbaPlanilha] = useState(null);
    const [selecionados, setSelecionados] = useState(new Set());
    // O envio que o modal confirma: 'emissao' (pedidos e observações) ou 'cancelamento'; null fechado.
    const [confirmar, setConfirmar] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [baixando, setBaixando] = useState(null);
    // O que o RSVP escreve para o Aéreo em cada linha: vai na entrada nova do pedido, ou é a anotação nova
    // da entrada que a linha tem.
    const [obsRsvp, setObsRsvp] = useState({});
    // Cancelamentos que a tela mudou, { [idParticipante]: { descricao } }: só marcam a linha como
    // "Cancelamento de emissão solicitado" — o status é o Aéreo que dá.
    const [cancelamentos, setCancelamentos] = useState({});
    // O Reenviar que a tela mudou em cada linha, { [idParticipante]: { observacao } }, ou null para tirar o
    // que estava salvo: a linha volta ao Aéreo na mesma linha, em A EMITIR, no Solicitar Emissão de Aéreo.
    const [reenvios, setReenvios] = useState({});
    // A tela mudou depois de carregar: o Salvar tem o que gravar.
    const [alterado, setAlterado] = useState(false);
    const [temSalvo, setTemSalvo] = useState(false);
    // Conta as recargas da tela: a faixa de status confere o filtro em cada uma (`ChipsLegenda`).
    const [recargas, setRecargas] = useState(0);
    // As anotações não enviadas que o X marcou, { [idParticipante]: [{ indice, texto }] }: saem no Salvar.
    const [excluidas, setExcluidas] = useState({});

    // Depois do Salvar e do Solicitar, o que estava na tela já está gravado: no rascunho, que volta na
    // linha (`rsvpPendente`), ou no pedido salvo (`restaurarSalvo`).
    const carregar = useCallback(() => {
        setCarregando(true);
        api({ method: 'get', action: '/eventoAereoEmissaoEnvio', token, params: { evento: idEvento } })
            .then(res => {
                const d = res.data.data ?? { participantes: [] };
                const lista = d.participantes ?? [];
                const salvo = restaurarSalvo(lista);
                setDados(d);
                setTemSalvo(salvo.temSalvo);
                setObsRsvp(salvo.observacoes);
                setCancelamentos({});
                setReenvios({});
                setExcluidas({});
                setAlterado(false);
                // Todos marcados: a solicitação é idempotente — ela sempre soma à emissão do evento, e
                // quem já foi vai de novo na requisição nova. Desmarcar é para quem fica de fora desta.
                // O cancelado pelo Aéreo é a exceção: pedir de novo é decisão do
                // RSVP (o checkbox ou o Solicitar Nova Emissão da linha), e ele começa de fora. Com algo salvo, a marca é
                // a do Salvar: quem não foi pedido ainda só fica marcado com o pedido salvo.
                setSelecionados(new Set(d.podeEnviar
                    ? lista.filter(p => !p.motivoNaoEnviar && (salvo.temSalvo
                        ? salvo.comPedido.has(p.id) || (!!p.emissao && !cancelada(p.emissao))
                        : !cancelada(p.emissao))).map(p => p.id)
                    : []));
                // Por último: a faixa de status confere o filtro com a seleção nova. O React 17 não agrupa estes
                // setters num `.then`, e cada um já desenha a tela.
                setRecargas(n => n + 1);
            })
            .catch(err => notify.apiError(err, 'Erro ao carregar os participantes'))
            .finally(() => setCarregando(false));
    }, [idEvento, token]);

    useEffect(() => {
        carregar();
        api({ method: 'get', action: '/eventoAereoCabecalho', token, params: { evento: idEvento } })
            .then(res => setHeader(res.data?.data ?? {}))
            .catch(() => {});
    }, [carregar, idEvento, token]);

    // Trilha fixa Evento → Participantes → Solicitar: a tela abre só pelo painel de participantes,
    // e o RSVP chega nela por caminhos diferentes (RSVP, evento, participantes).
    const setFullTrail = useSetFullBreadcrumbTrail();
    useEffect(() => {
        if (!header.nomeEvento) return;
        setFullTrail([
            { path: `/evento?idEvento=${idEvento}`, label: header.nomeEvento },
            { path: `/eventoParticipante/${idEvento}`, label: 'Participantes' },
            { path: `/cadastroEventoEmissaoEnvioAereo/${idEvento}`, label: 'Solicitar Emissão de Aéreo' },
        ]);
    }, [header.nomeEvento, idEvento, setFullTrail]);

    const todos = dados.participantes ?? [];
    // Os filtros valem juntos e propagam entre si, como na Emissão de Aéreo: a origem conta com o status e a
    // busca, e as chips contam o que passa no painel (`passaNosFiltros`). Escolher um tira o que ficou sem
    // nenhuma linha equivalente (`filtrosSemEquivalente`).
    const tela = { cancelamentos, marcados: dados.podeEnviar ? selecionados : null };
    // A aba que o Aéreo excluiu sai da lista na recarga, e a tela volta à Todos.
    const abaEscolhida = (dados.abas ?? []).find(a => a.id === abaPlanilha) ?? null;
    const filtros = { status: filtro, busca, origem: filtroOrigem, aba: abaEscolhida };
    const semOFiltro = (chave) => todos.filter(l => passaNosFiltros(l, filtros, tela, chave));
    const definir = { status: setFiltro, busca: setBusca, origem: setFiltroOrigem, aba: (a) => setAbaPlanilha(a ? a.id : null) };
    const semValor = { status: null, busca: '', origem: null, aba: null };
    const filtrar = (chave, valor) => {
        definir[chave](valor);
        filtrosSemEquivalente(todos, { ...filtros, [chave]: valor }, chave, tela).forEach(outro => definir[outro](semValor[outro]));
    };
    const selecao = {
        marcados: selecionados,
        // Linha com cancelamento em rascunho não vai como pedido no mesmo envio. Quem já está numa
        // requisição também não se desmarca: ele vai de novo, porque a solicitação sempre soma. O
        // cancelado se marca e desmarca livre: voltar a pedir a emissão dele é escolha do RSVP.
        selecionavel: (p) => dados.podeEnviar && !p.motivoNaoEnviar && !cancelamentos[p.id]
            && (!p.emissao || cancelada(p.emissao)),
        alternar: (id) => {
            setAlterado(true);
            setSelecionados(prev => {
                const novo = new Set(prev);
                if (novo.has(id)) novo.delete(id); else novo.add(id);
                return novo;
            });
        },
        marcarVarios: (ids, marcado) => {
            setAlterado(true);
            setSelecionados(prev => {
                const novo = new Set(prev);
                ids.forEach(id => { if (marcado) novo.add(id); else novo.delete(id); });
                return novo;
            });
        },
    };

    // O corpo do Salvar e do Solicitar: um item por participante que a tela mudou, { id, acao, observacao,
    // excluirObservacoes }. A `acao` é `emissao` (o marcado que abre linha nova), `cancelamento` ou `reenvio`;
    // null tira a que estava salva; sem ela, só a observação, que é sempre uma entrada própria. O back mexe só em
    // quem vem aqui.
    const corpoDoPedido = () => {
        const itens = new Map();
        const item = (id) => {
            if (!itens.has(id)) itens.set(id, { id });
            return itens.get(id);
        };
        // O null tira o que estava salvo, mas nunca a ação que a tela pôs na mesma linha: o cancelamento sobre um
        // Reenviar salvo é o cancelamento, e o back troca um pelo outro no rascunho.
        const tirar = (id) => {
            if (!item(id).acao) item(id).acao = null;
        };
        todos.forEach(p => {
            // O pedido novo leva a observação dele, vazia inclusive: vazia tira a que estava no pedido salvo.
            if (selecionados.has(p.id) && vaiNaRequisicao(p)) Object.assign(item(p.id), { acao: 'emissao', observacao: textoObs(p) });
            else if (p.pedidoSalvo) tirar(p.id);
        });
        Object.entries(cancelamentos).forEach(([id, c]) => (c ? Object.assign(item(id), { acao: 'cancelamento', observacao: c.descricao }) : tirar(id)));
        Object.entries(reenvios).forEach(([id, r]) => (r ? Object.assign(item(id), { acao: 'reenvio', observacao: r.observacao }) : tirar(id)));
        notas.forEach(p => {
            const texto = textoObs(p);
            if (texto !== '' && !itens.get(p.id)?.acao) item(p.id).observacao = texto;
        });
        Object.entries(excluidas).forEach(([id, marcadas]) => {
            if (marcadas.length) item(id).excluirObservacoes = marcadas;
        });
        return { evento: idEvento, participantes: [...itens.values()] };
    };

    // Grava sem mandar nada ao Aéreo: o pedido novo, a observação, o cancelamento e o Reenviar esperam o
    // Solicitar de cada um.
    const salvar = () => {
        setEnviando(true);
        return api({ method: 'post', action: '/eventoAereoEmissaoEnvioSalvar', token, data: corpoDoPedido() })
            .then(res => {
                if (res.data?.status === false) throw { response: res };
                toast.success('Salvo, sem mandar ao Aéreo: ele vê no Solicitar.');
                carregar();
                return true;
            })
            .catch(err => { notify.apiError(err, 'Erro ao salvar'); return false; })
            .finally(() => setEnviando(false));
    };

    // `envio`: 'emissao' manda os pedidos, os reenvios e as observações; 'cancelamento', só os
    // cancelamentos. O back grava a tela inteira antes, como o Salvar, e o do outro botão fica salvo.
    const solicitar = (envio) => {
        setEnviando(true);
        // Sem status: o RSVP pede a emissão, e o status é a resposta do Aéreo.
        api({ method: 'post', action: '/eventoAereoEmissaoEnvio', token, data: { ...corpoDoPedido(), envio } })
            .then(res => {
                if (res.data?.status === false) throw { response: res };
                toast.success(envio === 'cancelamento'
                    ? (nCancelamentos === 1 ? 'Cancelamento solicitado ao Aéreo' : 'Cancelamentos solicitados ao Aéreo')
                    : (efetivos
                        ? (efetivos === 1 ? 'Emissão solicitada para 1 participante' : `Emissão solicitada para ${efetivos} participantes`)
                        : nReenvios
                            ? (nReenvios === 1 ? 'Linha reenviada ao Aéreo' : `${nReenvios} linhas reenviadas ao Aéreo`)
                            : 'Observação enviada ao Aéreo'));
                setConfirmar(null);
                carregar();
            })
            .catch(err => notify.apiError(err, envio === 'cancelamento' ? 'Erro ao solicitar o cancelamento' : 'Erro ao solicitar a emissão'))
            .finally(() => setEnviando(false));
    };

    // O X da observação que ainda está no rascunho do RSVP: marca (ou desmarca) a exclusão, que vai no Salvar
    // com o resto. A enviada não se exclui.
    const alternarExclusao = (linha, nota) => {
        setAlterado(true);
        setExcluidas(prev => {
            const marcadas = prev[linha.id] ?? [];
            const mesma = (x) => x.indice === nota.indice && x.texto === nota.texto;
            return { ...prev, [linha.id]: marcadas.some(mesma) ? marcadas.filter(x => !mesma(x)) : [...marcadas, { indice: nota.indice, texto: nota.texto }] };
        });
    };

    // Downloads gerados na hora (cópia editada, planilha), com o botão em loading. `corpo`: POST.
    const baixar = async (chave, url, nomePadrao, corpo = null) => {
        setBaixando(chave);
        try {
            await baixarBlob(url, token, nomePadrao, corpo);
        } finally {
            setBaixando(null);
        }
    };

    const textoObs = (p) => String(obsRsvp[p.id] ?? '').trim();
    // Nova: diferente da anotação mais nova do RSVP na entrada, que já traz a do rascunho.
    const obsNova = (p) => !!p.emissao && textoObs(p) !== ''
        && textoObs(p) !== textoMaisNovo(p.emissao.observacaoRsvp).trim();
    // O que já está no rascunho da linha (`rsvpPendente`) conta no botão que o envia, como o que a tela
    // mudou: o cancelamento no dele; o Reenviar e a observação, no da emissão. O Reenviar tirado na tela
    // (null) e o que a tela trocou por outro não contam.
    const salvoNaLinha = (p, tipo) => p.emissao?.rsvpPendente === tipo;
    const naRequisicao = todos.filter(p => selecionados.has(p.id) && vaiNaRequisicao(p));
    const efetivos = naRequisicao.length;
    // Observação nova de quem não abre entrada — o marcado sem mudança, ou o cancelado que não foi
    // pedido de novo: vai na entrada que ele tem, sem requisição nova.
    // Quem tem Reenviar na tela vai com a observação dele, e não conta aqui.
    const notas = todos.filter(p => !cancelamentos[p.id] && !reenvios[p.id] && !naRequisicao.includes(p)
        && (obsNova(p) || salvoNaLinha(p, 'observacao')));
    const nNotas = notas.length;
    const semMudanca = todos.filter(p => selecionados.has(p.id) && !reenvios[p.id] && !vaiNaRequisicao(p) && !obsNova(p)
        && !p.emissao?.rsvpPendente).length;
    const nCancelamentos = new Set([...Object.keys(cancelamentos).filter(id => cancelamentos[id]),
        ...todos.filter(p => salvoNaLinha(p, 'cancelamento') && cancelamentos[p.id] !== null && !reenvios[p.id]).map(p => p.id)]).size;
    const nReenvios = new Set([...Object.keys(reenvios).filter(id => reenvios[id]),
        ...todos.filter(p => salvoNaLinha(p, 'reenvio') && reenvios[p.id] !== null && !cancelamentos[p.id]).map(p => p.id)]).size;
    // O Salvar tem o que gravar: a tela mudou, ou nada foi salvo ainda e a tela já tem o que mandar
    // (as marcas de sempre, ao abrir).
    const podeSalvar = !!dados.podeEnviar && (alterado || (!temSalvo && efetivos > 0));
    const baixarPlanilha = async () => {
        // A planilha é a da tela: o que a tela tem e não está salvo — o que a pessoa mudou, ou as marcas de
        // sempre, ao abrir — o Salvar grava antes, com os marcados, sem mandar nada ao Aéreo. Ela sai com o
        // rascunho do RSVP e com o pedido de quem está marcado, em A EMITIR. `rsvp` pede a versão desta tela,
        // com o que o Aéreo já enviou.
        if (podeSalvar && !(await salvar())) return;
        baixar('planilha', `${API}/eventoAereoEmissaoPlanilha?evento=${idEvento}&rsvp=1`, 'Emissao-Aereo.xlsx');
    };
    const proximaOrdem = (dados.requisicoes ?? []).length + 1;
    const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;
    const emLista = (itens) => (itens.length > 1 ? `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}` : (itens[0] ?? ''));
    // O que o envio da emissão leva além da requisição, para o título do modal.
    const extrasDoEnvio = [nReenvios && `reenviar ${plural(nReenvios, 'linha', 'linhas')}`, nNotas && `enviar ${plural(nNotas, 'observação', 'observações')}`].filter(Boolean);

    return (
        <ErpTemplate processando={carregando}>
            <div className="main-content container-fluid fin-pp">
                <CabecalhoEventoAereo
                    titulo="Solicitar Emissão de Aéreo"
                    nomeEvento={header.nomeEvento}
                    cliente={header.cliente}
                    solicitante={header.solicitante}
                    dataEvento={header.dataEvento}
                    localEvento={header.localEvento}
                    // Esta tela abre só pelo painel de participantes; o atalho leva de volta a ele.
                    atalhos={[
                        atendimento.atalho,
                        { rotulo: 'Participantes', titulo: 'Painel de participantes do evento.', to: '/eventoParticipante/' + idEvento },
                        { rotulo: 'Opções de Aéreo', titulo: 'Opções de aéreo do evento.', to: '/cadastroEventoRsvpOpcaoAereo/' + idEvento },
                        // A sugestão de voos que o Aéreo mandou no Enviar dele: para importar a um participante ou a todos.
                        dados.sugestao?.liberada && {
                            rotulo: 'Sugestão de Voos', titulo: 'Voos que o Aéreo sugeriu: importe para um participante ou para todos.',
                            to: '/cadastroEventoSugestaoAereo/' + idEvento,
                        },
                    ]}
                    // O mesmo painel da Emissão de Aéreo: o da malha mais recente e, vazio, o do cadastro do evento.
                    painel={[
                        { rotulo: 'Centro de Custo', valor: header.centroCusto, icone: <FaBuilding size={11} /> },
                        { rotulo: 'Ordem Interna (WBS)', valor: header.ordemInterna, icone: <FaHashtag size={11} /> },
                    ]}
                />

                <div className="fin-pp__card mb-3" style={{ padding: 16 }}>
                    {/* A busca e a origem em cima; embaixo, à direita, TKT médio e teto do cliente. Sem filtro
                        por requisição: a tela é do evento inteiro, e a solicitação sempre soma à emissão. TKT
                        emitidos, reembolso, total, total sem FEE e total de FEE ficam só com o Aéreo; o RSVP
                        tem a planilha inteira na exportação. */}
                    <div className="fin-pp__filters">
                        <div className="fin-pp__field fin-pp__field--auto" style={{ minWidth: 260, flex: '1 1 260px' }}>
                            <label className="fin-pp__label">Participante</label>
                            <div style={{ position: 'relative' }}>
                                <FaSearch size={12} style={{ position: 'absolute', left: 10, top: 12, color: '#94a3b8' }} />
                                <input className="fin-pp__input" style={{ paddingLeft: 30 }} value={busca} placeholder="Buscar pelo nome"
                                    onChange={e => filtrar('busca', e.target.value)} />
                            </div>
                        </div>
                        <SelectOrigem linhas={todos} contarEm={semOFiltro('origem')} filtro={filtroOrigem} onFiltrar={v => filtrar('origem', v)} />
                    </div>
                    <div className="d-flex justify-content-end" style={{ marginTop: 16 }}>
                        <TotaisPlanilha linhas={todos} tetoTktMedio={dados.tetoTktMedio} so={['TKT médio', 'Teto TKT médio cliente']} />
                    </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                    {/* As chips contam as linhas que passam na busca e na origem: a escolha lá propaga para cá. */}
                    <ChipsLegenda linhas={semOFiltro('status')} legenda={dados.legenda} filtro={filtro} onFiltrar={r => filtrar('status', r)} conferir={recargas}
                        tela={tela} />
                </div>

                <div className="fin-pp__card fin-pp__card--clip mb-5">
                    <PlanilhaEmissaoAereo modo="rsvp" linhas={todos} legenda={dados.legenda} filtro={filtro} filtroOrigem={filtroOrigem} filtroAba={abaEscolhida} busca={busca}
                        selecao={dados.podeEnviar ? selecao : null} baixando={baixando}
                        observacoesRsvp={obsRsvp}
                        onObservacaoRsvp={(id, texto) => {
                            setAlterado(true);
                            setObsRsvp(prev => ({ ...prev, [id]: texto }));
                            // Linha sem entrada que ninguém marcou: escrever a marca, porque a observação
                            // dela só tem destino no pedido.
                            if (String(texto ?? '').trim() !== '' && !todos.find(p => p.id === id)?.emissao) {
                                setSelecionados(prev => new Set(prev).add(id));
                            }
                        }}
                        cancelamentos={cancelamentos}
                        onCancelar={(linha, c) => {
                            setAlterado(true);
                            // Tirar o cancelamento: o da tela sai; o salvo vai como null, e o Salvar o tira do rascunho.
                            // A marca que o Cancelar tirou volta, como ao abrir a tela: a linha que o Aéreo tem e não
                            // cancelou fica marcada, e é a marca que deixa escrever a observação dela.
                            if (!c) {
                                setCancelamentos(prev => {
                                    const novo = { ...prev };
                                    if (linha.emissao?.rsvpPendente === 'cancelamento') novo[linha.id] = null; else delete novo[linha.id];
                                    return novo;
                                });
                                if (linha.emissao && !cancelada(linha.emissao) && !linha.motivoNaoEnviar) {
                                    setSelecionados(prev => new Set(prev).add(linha.id));
                                }
                                return;
                            }
                            // Só a descrição: o RSVP não escolhe o tipo do cancelamento.
                            setCancelamentos(prev => ({ ...prev, [linha.id]: { descricao: c.descricao } }));
                            setSelecionados(prev => { const novo = new Set(prev); novo.delete(linha.id); return novo; });
                            // Cancelar desfaz o Reenviar da tela. O salvo não precisa de null: o Salvar grava o
                            // cancelamento no lugar dele, e o null levaria o cancelamento junto.
                            setReenvios(prev => {
                                const novo = { ...prev };
                                delete novo[linha.id];
                                return novo;
                            });
                        }}
                        // O Reenviar da linha: null tira o da tela e, com um salvo, marca para o Salvar tirá-lo do rascunho.
                        reenvios={reenvios}
                        onReenviar={(linha, r) => {
                            setAlterado(true);
                            setReenvios(prev => {
                                const novo = { ...prev };
                                if (r) novo[linha.id] = r;
                                else if (linha.emissao?.rsvpPendente === 'reenvio') novo[linha.id] = null;
                                else delete novo[linha.id];
                                return novo;
                            });
                        }}
                        // A versão do RSVP: sem nenhum valor, nem o total do rodapé.
                        onBaixarEditada={(b) => baixar('editada:' + b.id, `${API}/eventoAereoEmissaoBilheteEditado?bilhetes=${b.id}&semValores=1`, 'bilhete-editado.pdf')}
                        observacoesExcluidas={excluidas}
                        onExcluirObservacao={dados.podeEnviar ? alternarExclusao : null} />
                    {/* As abas da planilha que o Aéreo criou, como as do Excel: o rodapé da tabela, cada uma com o
                        total dela. */}
                    <AbasDaPlanilha abas={dados.abas} linhas={todos} contarEm={todos} aba={abaEscolhida} onAba={a => filtrar('aba', a)} />
                </div>

                <div className="row fixoRodape">
                    <div className="col-12 d-flex align-items-center justify-content-end gap-3 flex-wrap">
                        {/* Antes da 1ª requisição, libera com pedido marcado ou salvo: a planilha já o mostra. */}
                        <button type="button" className="fin-pp__btn fin-pp__btn--pill"
                            disabled={(!dados.liberadaEmissao && !efetivos && !temSalvo) || baixando === 'planilha' || enviando}
                            onClick={baixarPlanilha}>
                            {baixando === 'planilha' ? <SpinnerBotao /> : <FaFileExcel size={12} style={{ marginRight: 6 }} />} Planilha de Emissão
                        </button>
                        {dados.podeEnviar && (
                            <>
                                <span className="fin-pp__cell-muted"
                                    title={semMudanca > 0 || nNotas > 0 ? 'Marcados sem nada novo para o Aéreo ficam como estão: o reenvio não muda o status que ele deu. Uma observação nova vai na linha que ele já tem, sem requisição nova.' : undefined}>
                                    {efetivos} {efetivos === 1 ? 'vai' : 'vão'} na requisição
                                    {nReenvios > 0 && ` · ${plural(nReenvios, 'reenvio', 'reenvios')}`}
                                    {nNotas > 0 && ` · ${plural(nNotas, 'observação nova', 'observações novas')}`}
                                    {semMudanca > 0 && ` · ${semMudanca} sem mudança`}
                                    {nCancelamentos > 0 && ` · ${nCancelamentos} cancelamento${nCancelamentos !== 1 ? 's' : ''}`}
                                </span>
                                {/* Grava sem mandar nada ao Aéreo: tudo espera o Solicitar de cada um. */}
                                <button type="button" className="fin-pp__btn fin-pp__btn--pill"
                                    title="Grava sem mandar nada ao Aéreo. O pedido novo, a observação, o cancelamento e o reenvio só vão a ele no Solicitar de cada um, com o e-mail."
                                    disabled={!podeSalvar || enviando} onClick={salvar}>
                                    {enviando && !confirmar ? <SpinnerBotao /> : <FaSave size={12} style={{ marginRight: 6 }} />} Salvar
                                </button>
                                {/* O cancelamento vai no botão dele: este leva os pedidos de emissão que
                                    mudaram, os reenvios e as observações novas. */}
                                <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--primary"
                                    disabled={(!efetivos && !nNotas && !nReenvios) || enviando} onClick={() => setConfirmar('emissao')}>
                                    <FaPaperPlane size={12} style={{ marginRight: 6 }} />
                                    Solicitar Emissão de Aéreo ({efetivos + nReenvios + nNotas})
                                </button>
                                {/* Só aparece com cancelamento na tela, e manda só os cancelamentos. */}
                                {nCancelamentos > 0 && (
                                    <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--danger"
                                        disabled={enviando} onClick={() => setConfirmar('cancelamento')}>
                                        <FaPaperPlane size={12} style={{ marginRight: 6 }} />
                                        Solicitar Cancelamento de Emissão ({nCancelamentos})
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <ConfirmarAcaoModal
                    aberto={confirmar === 'emissao'}
                    titulo={efetivos
                        ? `Solicitar a emissão de ${plural(efetivos, 'participante', 'participantes')}${extrasDoEnvio.length ? ` e ${emLista(extrasDoEnvio)}` : ''}?`
                        : `${emLista(extrasDoEnvio).replace(/^./, c => c.toUpperCase())} ao Aéreo?`}
                    subtitulo={[
                        efetivos ? `Abre a ${proximaOrdem}ª requisição de aéreo do evento. A tela de Emissão de Aéreo libera para o Aéreo subir os bilhetes.` : null,
                        efetivos && semMudanca > 0 ? `${semMudanca === 1 ? 'O marcado sem mudança fica' : `Os ${semMudanca} marcados sem mudança ficam`} como está, com o status que o Aéreo deu.` : null,
                        nReenvios ? 'Cada linha reenviada volta para EMITIR na mesma linha, com a sua observação; quem trocou o voo vai com o voo novo.' : null,
                        nNotas ? 'A observação nova vai na linha de cada um, sem mudar o status.' : null,
                        efetivos ? null : 'Não abre requisição nova.',
                        nCancelamentos ? 'Os cancelamentos ficam salvos, para o Solicitar Cancelamento de Emissão.' : null,
                    ].filter(Boolean).join(' ')}
                    notifica={efetivos
                        ? `O Aéreo recebe o e-mail "${proximaOrdem}ª Requisição de Aéreo - ${header.nomeEvento ?? ''}", com ${emLista(['a lista dos participantes', nReenvios && 'as linhas reenviadas', nNotas && 'as observações novas'].filter(Boolean))}${proximaOrdem > 1 ? ', em resposta ao e-mail da requisição anterior' : ''}.`
                        : `O Aéreo recebe o e-mail "${nReenvios && nNotas ? 'Atualização do RSVP' : nReenvios ? 'Reenvio do RSVP' : 'Observação do RSVP'}", com a requisição das linhas no assunto e ${emLista([nReenvios && 'cada linha reenviada', nNotas && 'a observação nova de cada um'].filter(Boolean))}.`}
                    rotulo="Solicitar" tom="primary" icone="enviar"
                    processando={enviando}
                    onConfirmar={() => solicitar('emissao')}
                    onFechar={() => setConfirmar(null)}
                />
                <ConfirmarAcaoModal
                    aberto={confirmar === 'cancelamento'}
                    titulo={`Solicitar o cancelamento da emissão de ${plural(nCancelamentos, 'participante', 'participantes')}?`}
                    subtitulo={`Cada linha fica marcada como Cancelamento de emissão solicitado, com a sua descrição, sem mudar o status: o tipo do cancelamento quem escolhe é o Aéreo. Não abre requisição nova.${efetivos || nNotas || nReenvios ? ' Os pedidos, as observações novas e os reenvios ficam salvos, para o Solicitar Emissão de Aéreo.' : ''}`}
                    notifica={`O Aéreo recebe o e-mail "Cancelamento de Emissão Solicitado", com a requisição das linhas no assunto e a descrição de cada cancelamento, em resposta ao último e-mail da conversa.`}
                    rotulo="Solicitar cancelamento" tom="danger" icone="enviar"
                    processando={enviando}
                    onConfirmar={() => solicitar('cancelamento')}
                    onFechar={() => setConfirmar(null)}
                />
            </div>
            {atendimento.modal}
        </ErpTemplate>
    );
}
